import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  useTheme,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  IconButton,
} from '@mui/material';
import {
  fetchTypeBasedConnection,
  fetchJiraProjects,
  addProjectKeyToJiraConnection,
} from '../apis/Connections';
import { fetchModulesBySource, fetchS3Objects } from '../apis/EntitiesSelection';
import { ThemeContext } from '../Context/ThemeContext';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';


const NodeConfigurationPanel = ({
  selectedNode,
  setSelectedNode,
  updateNodeData,
  onConfigUpdate,
}) => {
  const [connectionsList, setConnectionsList] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [errors, setErrors] = useState({});
  const [selectedModule, setSelectedModule] = useState('');
  const [moduleOptions, setModuleOptions] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [loadingModules, setLoadingModules] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState('');

  const [jiraProjects, setJiraProjects] = useState([]);
  const [loadingJiraProjects, setLoadingJiraProjects] = useState(false);
  const [selectedJiraProject, setSelectedJiraProject] = useState('');
  const [selectedJiraProjectDetails, setSelectedJiraProjectDetails] =
    useState(null);

  // S3 Object Selection State
  const [s3ObjectsDialogOpen, setS3ObjectsDialogOpen] = useState(false);
  const [s3Objects, setS3Objects] = useState([]);
  const [loadingS3Objects, setLoadingS3Objects] = useState(false);
  const [selectedS3Objects, setSelectedS3Objects] = useState([]);
  const [s3SearchTerm, setS3SearchTerm] = useState('');

  const rawLabel =
    selectedNode?.data?.label || selectedNode?.data?.config?.label || '';
  const type = rawLabel.split('(')[0]?.trim()?.toLowerCase();
  const isJiraNode = type === 'jira';
  const isSqlNode = type === 'sql';
  // Streaming connectors: Kafka, S3, Local File, SFTP
  const streamingConnectors = ['kafka', 's3', 'local file', 'localfile', 'sftp'];
  const isStreamingConnector = streamingConnectors.includes(type);
  const isS3SourceNode = isStreamingConnector && type === 's3' && 
    (selectedNode?.type === 'sourceNode' || selectedNode?.type === 'source');

  const getConnectorName = (conn) =>
    (conn?.connector_name || conn?.connector || '').toLowerCase();

  const buildSqlMetadata = (conn) => {
    if (!conn) return null;
    if (getConnectorName(conn) !== 'sql') return null;
    const params = conn?.custom_params || {};
    return {
      token: params?.token || conn?.token || null,
      host: params?.host || '',
      port: params?.port ? String(params.port) : '',
      database:
        params?.database_name || params?.db_name || params?.database || '',
    };
  };
  // Initialize from existing node data if available
  useEffect(() => {
    if (!selectedNode) return;

    // Reset state when switching nodes
    setConnectionsList([]);
    setSelectedConnection(null);
    setSelectedModule('');
    setModuleOptions([]);
    setErrors({});
    setLoadingConnections(true);
    setLoadingModules(true);

    const initialize = async () => {
      if (!type) {
        setLoadingConnections(false);
        setLoadingModules(false);
        return;
      }

      try {
        const connections = await fetchTypeBasedConnection(type);
        setConnectionsList(connections);

        let modules = [];
        if (!isJiraNode && !isStreamingConnector) {
          if (isSqlNode) {
            modules = ['SQL'];
          } else {
            modules = await fetchModulesBySource(type);
          }
        }

        setModuleOptions(modules);

        const config = selectedNode?.data?.config;

        if (config?.connection) {
          let matchedConn = connections.find(
            (conn) => conn.conn_id === config.connection.conn_id
          );

          if (!matchedConn && config.connection.conn_name) {
            matchedConn = connections.find(
              (conn) => conn.conn_name === config.connection.conn_name
            );
          }

          if (matchedConn) {
            setSelectedConnection(matchedConn || null);
            setSelectedEnvironment(
              typeof matchedConn.environment === 'string'
                ? matchedConn.environment.toLowerCase()
                : ''
            );
          }

          console.log('config:::', config);

          console.log('connections =', connections);
          console.log('matchedConn?.environment =', matchedConn?.environment);
          console.log('selectedEnvironment BEFORE set =', selectedEnvironment);
        }

        // Initialize S3 objects if already selected
        if (isS3SourceNode && config?.s3Objects && Array.isArray(config.s3Objects)) {
          setSelectedS3Objects(config.s3Objects);
        }

        // Set module with matching logic to find the correct option from moduleOptions
        if (config?.module && !isJiraNode) {
          const configModule = config.module;
          const configModuleLower = configModule.toLowerCase();
          let matchedModule = null;
          
          if (isSqlNode) {
            // For SQL, always use uppercase
            matchedModule = 'SQL';
          } else if (modules && modules.length > 0) {
            // Try exact match first (case-insensitive)
            matchedModule = modules.find(
              (mod) => mod.toLowerCase() === configModuleLower
            );
            
            // If no exact match, try partial match (for cases like "salesforce financial services cloud")
            if (!matchedModule) {
              matchedModule = modules.find((mod) => {
                const modLower = mod.toLowerCase();
                // Check if config module contains the option or vice versa
                return modLower.includes(configModuleLower) || 
                       configModuleLower.includes(modLower) ||
                       // Check if both contain "salesforce" or other common terms
                       (configModuleLower.includes('salesforce') && modLower.includes('salesforce'));
              });
            }
            
            // If still no match, try to find by first word (e.g., "salesforce" from "salesforce financial services cloud")
            if (!matchedModule) {
              const firstWord = configModuleLower.split(' ')[0];
              matchedModule = modules.find((mod) => 
                mod.toLowerCase().startsWith(firstWord) || 
                firstWord === mod.toLowerCase()
              );
            }
            
            // Fallback: use the exact value from config if no match found
            if (!matchedModule) {
              matchedModule = configModule;
            }
          } else {
            // No modules loaded yet, use config value
            matchedModule = configModule;
          }
          
          console.log('Module matching - config.module:', configModule);
          console.log('Module matching - available modules:', modules);
          console.log('Module matching - matched module:', matchedModule);
          
          setSelectedModule(matchedModule);
        } else if (isSqlNode && !config?.module) {
          setSelectedModule('SQL');
        }
      } catch (error) {
        console.error('❌ Error loading node config:', error);
      } finally {
        setLoadingConnections(false);
        setLoadingModules(false);
      }
    };

    initialize();
  }, [selectedNode]);

  const theme = useTheme();
  const { darkMode } = useContext(ThemeContext);

  const fetchConnections = async () => {
    if (!type) return;
    setLoadingConnections(true);
    const connections = await fetchTypeBasedConnection(type);
    setConnectionsList(connections);
    setLoadingConnections(false);
  };

  if (!selectedNode) return null; // Hide panel when no node is selected

  const fetchModules = async () => {
    if (isJiraNode) return;
    if (!type) return;
    setLoadingModules(true);
    if (isSqlNode) {
      setModuleOptions(['SQL']);
      setLoadingModules(false);
      return;
    }
    const modules = await fetchModulesBySource(type);
    setModuleOptions(modules);
    setLoadingModules(false);
  };

  const loadS3Objects = async (connId) => {
    if (!connId) return;
    setLoadingS3Objects(true);
    try {
      const objects = await fetchS3Objects(connId);
      setS3Objects(Array.isArray(objects) ? objects : []);
    } catch (error) {
      console.error('Failed to load S3 objects:', error);
      setS3Objects([]);
    } finally {
      setLoadingS3Objects(false);
    }
  };

  const handleS3ObjectToggle = (objectName) => {
    setSelectedS3Objects((prev) => {
      if (prev.includes(objectName)) {
        return prev.filter((name) => name !== objectName);
      } else {
        return [...prev, objectName];
      }
    });
  };

  const handleSaveS3Objects = () => {
    if (selectedS3Objects.length === 0) {
      setErrors((prev) => ({ ...prev, s3Objects: 'Please select at least one S3 object' }));
      return;
    }

    // Update node data with selected S3 objects
    updateNodeData(selectedNode.id, {
      ...selectedNode?.data?.config,
      s3Objects: selectedS3Objects,
    });

    setS3ObjectsDialogOpen(false);
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.s3Objects;
      return newErrors;
    });
  };

  useEffect(() => {
    const loadProjects = async () => {
      if (!isJiraNode) return; // ✅ guard clause

      setLoadingJiraProjects(true);
      if (selectedConnection?.conn_id) {
        try {
          const data = await fetchJiraProjects(selectedConnection.conn_id);
          setJiraProjects(Array.isArray(data) ? data : []);
        } catch (error) {
          console.error('Error loading Jira projects:', error);
          setJiraProjects([]);
        }
      } else {
        setJiraProjects([]);
      }
      setLoadingJiraProjects(false);
    };

    loadProjects();
  }, [selectedConnection?.conn_id, isJiraNode]);

  // Validation function
  const validateForm = () => {
    let newErrors = {};
    if (!selectedConnection) {
      newErrors.connection = 'Connection is required';
    }

    if (!isJiraNode && !isStreamingConnector && !selectedModule) {
      newErrors.module = 'Module is required';
    }

    // For S3 source nodes, require S3 objects selection
    if (isS3SourceNode && (!selectedS3Objects || selectedS3Objects.length === 0)) {
      newErrors.s3Objects = 'Please select at least one S3 object';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return; // Stop if validation fails

    const baseConfig = {
      id: selectedNode.id,
      label: selectedNode?.data?.label || selectedNode?.data?.config?.label,
      type: selectedNode.type,
      ...selectedNode?.data?.config,
      connection: selectedConnection
        ? {
            conn_id: selectedConnection.conn_id,
            conn_name: selectedConnection.conn_name,
            client_id: selectedConnection.client_id,
            api_version: selectedConnection.api_version,
          }
        : null,
    };

    // ✅ Conditionally include module if not Jira and not streaming connector (SQL should send lowercase)
    if (!isJiraNode && !isStreamingConnector) {
      const normalizedModule = isSqlNode
        ? (selectedModule || 'SQL').toLowerCase()
        : selectedModule;
      baseConfig.module = normalizedModule;
    }

    // Include S3 objects for S3 source nodes
    if (isS3SourceNode && selectedS3Objects.length > 0) {
      baseConfig.s3Objects = selectedS3Objects;
    }

    if (isJiraNode && selectedJiraProjectDetails) {
      baseConfig.project = {
        key: selectedJiraProjectDetails.key,
        name: selectedJiraProjectDetails.name,
        id: selectedJiraProjectDetails.id,
      };
    }

    const payload = { config: baseConfig };

    console.log('Saving config:', payload);

    setSelectedNode(null); // Close the panel after saving
  };

  const getContainedButtonStyle = () => ({
    fontSize: '0.875rem',
    backgroundColor: '#3C9564',
    padding: '0.3rem 0.8rem',
    color: '#ffffff',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    borderRadius: 0.5,
    boxShadow: '0px 4px 10px 0px #0000000D',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.03)',
      backgroundColor: '#2d7a4d',
      boxShadow: '0px 4px 12px 0px rgba(60, 149, 100, 0.3)',
      border: '1px solid transparent',
    },
    '&:active': {
      backgroundColor: '#256a3f',
      boxShadow: '0px 4px 12px 0px rgba(60, 149, 100, 0.3)',
      border: '1px solid transparent',
    },
  });

  // Common dropdown styling to ensure equal width regardless of content
  const getDropdownStyle = (hasError = false) => ({
    mb: 2,
    borderRadius: '6px',
    backgroundColor: darkMode ? '#2a2a2a' : '#ffffff',
    height: '2.8rem',
    width: '100%',
    color: darkMode ? '#ffffff' : theme.palette.text.primary,
    transition: 'all 0.2s ease-in-out',
    '.MuiSelect-select': {
      color: darkMode ? '#ffffff' : theme.palette.text.primary,
      padding: '12px 16px',
      fontSize: '0.95rem',
      width: '100%', // Ensure select content takes full width
      textOverflow: 'ellipsis', // Handle long text gracefully
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : darkMode ? '#404040' : '#DDDFE3',
      borderWidth: '1px',
      borderRadius: '6px',                             
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : darkMode ? '#2196F3' : '#2196F3',
      borderWidth: '1px',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : '#2196F3',
      borderWidth: '1px',
      boxShadow: hasError
        ? '0 0 0 3px rgba(244, 67, 54, 0.1)'
        : darkMode
        ? '0 0 0 3px rgba(33, 150, 243, 0.1)'
        : '0 0 0 3px rgba(33, 150, 243, 0.1)',
    },
    '& .MuiSvgIcon-root': {
      color: darkMode ? '#ffffff' : '#666666',
      transition: 'transform 0.2s ease-in-out',
    },
    '&:hover .MuiSvgIcon-root': {
      color: '#2196F3',
    },
    boxShadow: darkMode
      ? '0 2px 8px rgba(0, 0, 0, 0.3)'
      : '0 2px 8px rgba(0, 0, 0, 0.08)',
  });

  // Common menu props for consistent dropdown behavior
  const getMenuProps = () => ({
    PaperProps: {
      sx: {
        backgroundColor: darkMode ? '#2a2a2a' : '#ffffff',
        color: darkMode ? '#ffffff' : theme.palette.text.primary,
        boxShadow: darkMode
          ? '0 8px 32px rgba(0, 0, 0, 0.4)'
          : '0 8px 32px rgba(0, 0, 0, 0.12)',
        mt: 1,
        borderRadius: '12px',
        border: darkMode ? '1px solid #404040' : '1px solid #e0e0e0',
        maxHeight: '300px',
        minWidth: '300px', // Ensure dropdown menu has consistent width
        '& .MuiMenuItem-root': {
          mx: 1,
          my: 0.5,
          borderRadius: '8px',
          fontSize: '0.95rem',
          fontWeight: 400,
          padding: '12px 16px',
          transition: 'all 0.15s ease-in-out',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          '&.Mui-selected': {
            backgroundColor: darkMode ? '#1976D2' : '#e3f2fd',
            color: darkMode ? '#ffffff' : '#1976D2',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: darkMode ? '#1565C0' : '#bbdefb',
            },
          },
          '&:hover': {
            backgroundColor: darkMode ? '#404040' : '#f5f5f5',
            transform: 'translateX(4px)',
          },
          '&.Mui-disabled': {
            color: darkMode ? '#666666' : '#999999',
            opacity: 0.6,
          },
        },
      },
    },
  });

  return (
    <Box
      sx={{
        maxWidth: '600px',
        // height: '95%',
        backgroundColor: darkMode ? '#1e1e1e' : theme.palette.background.paper,
        // boxShadow: darkMode
        //   ? '-5px 0 10px rgba(0, 0, 0, 0.5)'
        //   : '-5px 0 10px rgba(0, 0, 0, 0.2)',
        // borderLeft: darkMode
        //   ? '1px solid #333333'
        //   : `1px solid ${theme.palette.divider}`,
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        // transition: 'width 0.3s ease-in-out',
        color: darkMode ? '#ffffff' : theme.palette.text.primary,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontSize: 19 }}
          color={darkMode ? '#ffffff' : 'text.primary'}
        >
          Set Properties
        </Typography>

      </Box>

      {!isStreamingConnector && (
        <>
          <Typography
            variant="body1"
            sx={{ mb: 1, fontWeight: 500 }}
            color={darkMode ? '#ffffff' : 'text.primary'}
          >
            Select Environment
          </Typography>
          <Select
            fullWidth
            value={
              ['test', 'production'].includes(selectedEnvironment)
                ? selectedEnvironment
                : ''
            }
            onChange={(e) => {
              setSelectedEnvironment(e.target.value);
              setSelectedConnection(null);
            }}
            displayEmpty
            sx={getDropdownStyle()}
            MenuProps={getMenuProps()}
            IconComponent={KeyboardArrowDownIcon}
          >
            <MenuItem value="" disabled>
              Select Environment
            </MenuItem>

            <MenuItem value="test">Test</MenuItem>
            <MenuItem value="production">Production</MenuItem>
          </Select>
        </>
      )}

      <Typography
        variant="body1"
        sx={{ mb: 1, fontWeight: 500 }}
        color={darkMode ? '#ffffff' : 'text.primary'}
      >
        Select Connection
      </Typography>
      <Select
        fullWidth
        value={selectedConnection?.conn_id || ''}
        onOpen={fetchConnections}
        onChange={async (e) => {
          const selected = connectionsList.find(
            (conn) => conn.conn_id === e.target.value
          );
          if (!selected) return;
          setSelectedConnection(selected);

          const sqlMetadata = buildSqlMetadata(selected);

          // Directly update node data with minimal info
          updateNodeData(selectedNode.id, {
            connection: {
              conn_id: selected.conn_id,
              conn_name: selected.conn_name,
              client_id: selected.client_id,
              api_version: selected.api_version,
              connector_name:
                selected.connector_name || selected.connector || type?.toUpperCase() || '',
              environment: selected.environment,
            },
            sqlMetadata: sqlMetadata || null,
          });

          // For S3 source nodes, open S3 objects selection dialog
          if (isS3SourceNode && selected.conn_id) {
            setS3ObjectsDialogOpen(true);
            await loadS3Objects(selected.conn_id);
          }
        }}
        displayEmpty
        sx={getDropdownStyle(Boolean(errors.connection))}
        error={Boolean(errors.connection)}
        MenuProps={getMenuProps()}
        IconComponent={KeyboardArrowDownIcon}
      >
        <MenuItem value="" disabled>
          {loadingConnections ? (
            <Box display="flex" alignItems="center">
              <CircularProgress size={16} sx={{ mr: 1 }} /> Loading...
            </Box>
          ) : (
            'Select'
          )}
        </MenuItem>

        {!loadingConnections &&
          connectionsList
            .filter((conn) => {
              // For streaming connectors, don't filter by environment
              if (isStreamingConnector) return true;
              // For other connectors, filter by environment
              return selectedEnvironment === 'production'
                ? !conn.environment ||
                    conn.environment.toLowerCase() === 'production'
                : conn.environment?.toLowerCase() ===
                    selectedEnvironment?.toLowerCase();
            })
            .map((conn) => (
              <MenuItem key={conn.conn_id} value={conn.conn_id}>
                {conn.conn_name}
              </MenuItem>
            ))}
      </Select>

      {errors.connection && (
        <Typography variant="caption" color="error">
          {errors.connection}
        </Typography>
      )}

      {/* S3 Objects Selection Indicator */}
      {isS3SourceNode && selectedConnection && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: '8px',
            backgroundColor: darkMode ? '#2a3a4a' : '#e3f2fd',
            border: '1px solid',
            borderColor: darkMode ? '#3a4a5a' : '#90caf9',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: '#3C9564',
              mb: 1,
            }}
          >
            S3 Objects Selection Required
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              color: darkMode ? '#b0b0b0' : '#666',
              mb: 1.5,
            }}
          >
            {selectedS3Objects.length > 0
              ? `${selectedS3Objects.length} object${selectedS3Objects.length !== 1 ? 's' : ''} selected`
              : 'Please select S3 objects to use as data sources'}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={async () => {
              setS3ObjectsDialogOpen(true);
              await loadS3Objects(selectedConnection.conn_id);
            }}
            sx={{
              textTransform: 'none',
              borderColor: '#3C9564',
              color: '#3C9564',
              fontSize: '0.8125rem',
              '&:hover': {
                borderColor: '#3C9564',
                backgroundColor: '#DAEFFF',
                color: '#0F172A',
              },
            }}
          >
            {selectedS3Objects.length > 0 ? 'Change Selection' : 'Select S3 Objects'}
          </Button>
        </Box>
      )}

      {!isJiraNode && !isStreamingConnector && (
        <>
          <Typography
            variant="body1"
            sx={{ mb: 1, fontWeight: 500 }}
            color={darkMode ? '#ffffff' : 'text.primary'}
          >
            Select Module
          </Typography>
          <Select
            fullWidth
            value={selectedModule || ''}
            onChange={(e) => {
              const selected = e.target.value;
              setSelectedModule(selected);
              const normalizedModule = isSqlNode
                ? (selected || 'SQL').toLowerCase()
                : selected;

              const updatedConfig = {
                id: selectedNode.id,
                label:
                  selectedNode?.data?.label ||
                  selectedNode?.data?.config?.label,
                type: selectedNode.type,
                ...selectedNode?.data?.config,
                connection: selectedConnection
                  ? {
                      conn_id: selectedConnection.conn_id,
                      conn_name: selectedConnection.conn_name,
                      client_id: selectedConnection.client_id,
                      api_version: selectedConnection.api_version,
                    }
                  : null,
                module: normalizedModule,
              };

              console.log(
                '✅ Module Updated Config JSON:',
                JSON.stringify(updatedConfig, null, 2)
              );
              updateNodeData(selectedNode.id, updatedConfig);
              onConfigUpdate(updatedConfig);
            }}
            onOpen={fetchModules}
            displayEmpty
            sx={{ ...getDropdownStyle(), mb: 3 }}
            MenuProps={getMenuProps()}
            IconComponent={KeyboardArrowDownIcon}
          >
            <MenuItem value="" disabled>
              {loadingModules ? (
                <Box display="flex" alignItems="center">
                  <CircularProgress size={16} sx={{ mr: 1 }} /> Loading...
                </Box>
              ) : (
                'Select'
              )}
            </MenuItem>
            {!loadingModules &&
              Array.isArray(moduleOptions) &&
              moduleOptions.map((module) => (
                <MenuItem key={module} value={module}>
                  {module}
                </MenuItem>
              ))}
          </Select>
          {errors.module && (
            <Typography variant="caption" color="error" sx={{ mb: 2 }}>
              {errors.module}
            </Typography>
          )}
        </>
      )}

      {isJiraNode && (
        <>
          <Typography
            variant="body1"
            sx={{ mb: 1, fontWeight: 500 }}
            color={darkMode ? '#ffffff' : 'text.primary'}
          >
            Select Jira Project
          </Typography>

          <Select
            fullWidth
            value={selectedJiraProject}
            onChange={async (e) => {
              const projectKey = e.target.value;
              setSelectedJiraProject(projectKey);

              // Correctly find the selected project from loaded projects
              const selectedProjectObj = jiraProjects.find(
                (p) => p.key === projectKey || p.id === projectKey // fallback if you pass ID in <MenuItem>
              );

              console.log('selectedProjectObj', selectedProjectObj);
              setSelectedJiraProjectDetails(selectedProjectObj); // still update for persistence

              // ✅ Use selectedProjectObj directly instead of selectedJiraProjectDetails
              if (selectedProjectObj) {
                const updatedConfig = {
                  id: selectedNode.id,
                  label:
                    selectedNode?.data?.label ||
                    selectedNode?.data?.config?.label,
                  type: selectedNode.type,
                  ...selectedNode?.data?.config,
                  connection: selectedConnection
                    ? {
                        conn_id: selectedConnection.conn_id,
                        conn_name: selectedConnection.conn_name,
                        client_id: selectedConnection.client_id,
                        api_version: selectedConnection.api_version,
                      }
                    : null,
                  project: {
                    key: selectedProjectObj.key,
                    name: selectedProjectObj.name,
                    id: selectedProjectObj.id,
                  },
                };

                updateNodeData(selectedNode.id, updatedConfig);
                onConfigUpdate(updatedConfig);
                console.log(
                  '✅ Updated node config with Jira project',
                  updatedConfig
                );
              } else {
                console.warn(
                  '⚠️ Project not found in list for key:',
                  projectKey
                );
              }

              try {
                await addProjectKeyToJiraConnection(
                  selectedConnection?.conn_id,
                  projectKey
                );
                console.log(
                  '✅ Project key added to Jira connection successfully'
                );
              } catch (error) {
                console.error('❌ Failed to add project key:', error.message);
              }
            }}
            displayEmpty
            sx={{ ...getDropdownStyle(darkMode, theme), mb: 3 }}
            MenuProps={getMenuProps(darkMode, theme)}
          >
            <MenuItem value="" disabled>
              {loadingJiraProjects ? (
                <Box display="flex" alignItems="center">
                  <CircularProgress size={16} sx={{ mr: 1 }} /> Loading...
                </Box>
              ) : (
                'Select'
              )}
            </MenuItem>

            {!loadingJiraProjects &&
              jiraProjects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
          </Select>
        </>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          // mt: 'auto',
          // mb: 12,
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => setSelectedNode(null)}
          sx={{
            borderRadius: '0.5rem',
            padding: '0.3rem 0.8rem',
            fontSize: '0.875rem',
            color: '#0F172A',
            border: '1px solid #0F172A',
            background: 'transparent',
            '&:hover': {
              border: '1px solid #0F172A',
              color: '#0F172A',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          sx={getContainedButtonStyle(theme)}
        >
          Save
        </Button>
      </Box>

      {/* S3 Objects Selection Dialog */}
      <Dialog
        open={s3ObjectsDialogOpen}
        onClose={() => setS3ObjectsDialogOpen(false)}
        maxWidth="sm"
        fullWidth={false}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
            width: '500px',
            maxWidth: '90vw',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1.5,
            pt: 2,
            px: 2,
            borderBottom: darkMode ? '1px solid #333' : '1px solid #e0e0e0',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: '1.1rem',
              fontWeight: 600,
              color: darkMode ? '#ffffff' : 'text.primary',
            }}
          >
            Select S3 Objects
          </Typography>
          <IconButton
            onClick={() => setS3ObjectsDialogOpen(false)}
            sx={{ color: darkMode ? '#ffffff' : '#666' }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, px: 2, pb: 1 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1.5,
              color: darkMode ? '#b0b0b0' : '#666',
              fontSize: '0.8125rem',
            }}
          >
            Please select the S3 objects you want to use as data sources. You can select multiple objects.
          </Typography>

          {/* Search Box */}
          <TextField
            fullWidth
            placeholder="Search S3 objects..."
            value={s3SearchTerm}
            onChange={(e) => setS3SearchTerm(e.target.value)}
            size="small"
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                backgroundColor: darkMode ? '#2a2a2a' : '#f5f5f5',
                borderRadius: '6px',
                height: '36px',
                fontSize: '0.875rem',
              },
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 0.75, color: darkMode ? '#999' : '#666', fontSize: '18px' }} />,
            }}
          />

          {/* Loading State */}
          {loadingS3Objects ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 3,
              }}
            >
              <CircularProgress size={20} />
              <Typography sx={{ ml: 1.5, color: darkMode ? '#ffffff' : 'text.primary', fontSize: '0.875rem' }}>
                Loading S3 objects...
              </Typography>
            </Box>
          ) : (
            <>
              {/* Objects List */}
              <Box
                sx={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: darkMode ? '1px solid #333' : '1px solid #e0e0e0',
                  borderRadius: '6px',
                  backgroundColor: darkMode ? '#2a2a2a' : '#fafafa',
                }}
              >
                {s3Objects.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography color={darkMode ? '#999' : '#666'} fontSize="0.875rem">
                      No S3 objects found
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {s3Objects
                      .filter((obj) => {
                        const objKey = obj?.key || '';
                        return objKey.toLowerCase().includes(s3SearchTerm.toLowerCase());
                      })
                      .map((obj, index) => {
                        const objKey = obj?.key || '';
                        const isSelected = selectedS3Objects.includes(objKey);
                        return (
                          <ListItem
                            key={index}
                            disablePadding
                            sx={{
                              borderBottom: darkMode ? '1px solid #333' : '1px solid #e0e0e0',
                              '&:last-child': { borderBottom: 'none' },
                            }}
                          >
                            <ListItemButton
                              onClick={() => handleS3ObjectToggle(objKey)}
                              sx={{
                                py: 0.75,
                                px: 1,
                                '&:hover': {
                                  backgroundColor: darkMode ? '#333' : '#f0f0f0',
                                },
                              }}
                            >
                              <Checkbox
                                checked={isSelected}
                                size="small"
                                sx={{
                                  color: '#3C9564',
                                  '&.Mui-checked': {
                                    color: '#3C9564',
                                  },
                                  padding: '4px',
                                }}
                              />
                              <ListItemText
                                primary={objKey}
                                secondary={obj?.last_modified ? `Last modified: ${new Date(obj.last_modified).toLocaleString()}` : undefined}
                                sx={{
                                  '& .MuiListItemText-primary': {
                                    color: darkMode ? '#ffffff' : 'text.primary',
                                    fontSize: '0.875rem',
                                    fontFamily: 'monospace',
                                    wordBreak: 'break-all',
                                    lineHeight: 1.4,
                                  },
                                  '& .MuiListItemText-secondary': {
                                    color: darkMode ? '#999' : '#666',
                                    fontSize: '0.75rem',
                                    mt: 0.25,
                                  },
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                  </List>
                )}
              </Box>

              {/* Selected Count */}
              {selectedS3Objects.length > 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1.5,
                    color: '#3C9564',
                    fontWeight: 500,
                    fontSize: '0.8125rem',
                  }}
                >
                  {selectedS3Objects.length} object{selectedS3Objects.length !== 1 ? 's' : ''} selected
                </Typography>
              )}

              {/* Error Message */}
              {errors.s3Objects && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, display: 'block', fontSize: '0.75rem' }}
                >
                  {errors.s3Objects}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            borderTop: darkMode ? '1px solid #333' : '1px solid #e0e0e0',
          }}
        >
          <Button
            onClick={() => {
              setS3ObjectsDialogOpen(false);
              setS3SearchTerm('');
            }}
            sx={{
              color: '#0F172A',
              textTransform: 'none',
              fontSize: '0.875rem',
              minWidth: 'auto',
              px: 1.5,
              border: '1px solid #0F172A',
              '&:hover': {
                backgroundColor: '#DAEFFF',
                color: '#0F172A',
                border: '1px solid #0F172A',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveS3Objects}
            variant="contained"
            disabled={loadingS3Objects || selectedS3Objects.length === 0}
            sx={{
              backgroundColor: '#0F172A',
              color: '#ffffff',
              textTransform: 'none',
              fontSize: '0.875rem',
              minWidth: 'auto',
              px: 2,
              '&:hover': {
                backgroundColor: '#1a2332',
              },
              '&:disabled': {
                backgroundColor: darkMode ? '#333' : '#e0e0e0',
                color: darkMode ? '#666' : '#999',
              },
            }}
          >
            Save ({selectedS3Objects.length})
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

NodeConfigurationPanel.propTypes = {
  selectedNode: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    data: PropTypes.shape({
      label: PropTypes.string.isRequired,
      config: PropTypes.object, // Allow dynamic config shape
    }).isRequired,
  }),
  setSelectedNode: PropTypes.func.isRequired,
  updateNodeData: PropTypes.func.isRequired,
  onConfigUpdate: PropTypes.func.isRequired,
};

export default NodeConfigurationPanel;
